import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge, timer, of, EMPTY } from 'rxjs';
import { map, startWith, distinctUntilChanged, switchMap, catchError, debounceTime, tap, filter, takeUntil, shareReplay, timeout } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environment/environment';

@Injectable({
  providedIn: 'root'
})
export class ConnectivityService {
  private isOnline$ = new BehaviorSubject<boolean>(navigator.onLine);
  private lastConnectivityCheck = 0;
  private connectivityCheckInterval = 3000; // 3 seconds
  private isDestroyed$ = new BehaviorSubject<boolean>(false);
  
  // Debouncing and throttling controls
  private connectivityCheckDebounceTime = 1000; // 1 second
  private networkEventDebounceTime = 500; // 500ms
  
  // Enhanced state tracking
  private consecutiveFailures = 0;
  private maxConsecutiveFailures = 3;
  private isPerformingCheck = false;

  constructor(private http: HttpClient) {
    this.initializeConnectivityMonitoring();
    this.setupVisibilityChangeListener();
    this.setupPageFocusListener();
    
    // Perform initial connectivity check with delay to avoid race conditions
    setTimeout(() => {
      this.performConnectivityCheck().subscribe(isConnected => {
        this.updateConnectivityState(isConnected);
      });
    }, 500); // Increased delay to ensure app is fully initialized
  }

  private initializeConnectivityMonitoring(): void {
    // Listen to browser's online/offline events with debouncing
    const online$ = fromEvent(window, 'online').pipe(
      debounceTime(this.networkEventDebounceTime),
    );
    
    const offline$ = fromEvent(window, 'offline').pipe(
      debounceTime(this.networkEventDebounceTime),
    );
    
    // Handle online events with verification
    online$.pipe(
      takeUntil(this.isDestroyed$),
      switchMap(() => {
        return this.performConnectivityCheck().pipe(
          catchError(() => {
            return timer(2000).pipe(
              switchMap(() => this.performConnectivityCheck()),
              catchError(() => of(false))
            );
          })
        );
      })
    ).subscribe(isConnected => {
      this.updateConnectivityState(isConnected);
    });
    
    // Handle offline events - immediately set to offline but verify after delay
    offline$.pipe(
      takeUntil(this.isDestroyed$),
      tap(() => {
        this.updateConnectivityState(false);
      }),
      // Verify after a delay in case it was a temporary network hiccup
      switchMap(() => timer(3000).pipe(
        switchMap(() => this.performConnectivityCheck()),
        catchError(() => of(false))
      ))
    ).subscribe(isConnected => {
      if (isConnected) {
        this.updateConnectivityState(true);
      }
    });
  }

  private setupVisibilityChangeListener(): void {
    if (typeof document !== 'undefined') {
      fromEvent(document, 'visibilitychange').pipe(
        takeUntil(this.isDestroyed$),
        filter(() => !document.hidden),
        debounceTime(1000), // Debounce visibility changes
      ).subscribe(() => {
        // Reset consecutive failures when app becomes visible
        this.consecutiveFailures = 0;
        
        this.performConnectivityCheck().subscribe(isConnected => {
          this.updateConnectivityState(isConnected);
        });
      });
    }
  }

  private setupPageFocusListener(): void {
    if (typeof window !== 'undefined') {
      fromEvent(window, 'focus').pipe(
        takeUntil(this.isDestroyed$),
        debounceTime(1000),
      ).subscribe(() => {
        // Reset state on focus to handle computer wake scenarios
        this.consecutiveFailures = 0;
        
        this.performConnectivityCheck().subscribe(isConnected => {
          this.updateConnectivityState(isConnected);
        });
      });
    }
  }

  /**
   * Get current connectivity status as Observable
   */
  getConnectivityStatus(): Observable<boolean> {
    return this.isOnline$.pipe(
      distinctUntilChanged(),
      shareReplay(1)
    );
  }

  /**
   * Get current connectivity status synchronously
   */
  isConnected(): boolean {
    return this.isOnline$.value;
  }

  /**
   * Force a connectivity check with proper throttling
   */
  checkConnectivity(): Observable<boolean> {
    return this.performConnectivityCheck().pipe(
      tap(isConnected => this.updateConnectivityState(isConnected))
    );
  }

  /**
   * Perform actual connectivity test with enhanced reliability
   */
  private performConnectivityCheck(): Observable<boolean> {
    const now = Date.now();
    
    // Prevent overlapping checks
    if (this.isPerformingCheck) {
      return of(this.isOnline$.value);
    }
    
    // Throttle connectivity checks
    if (now - this.lastConnectivityCheck < this.connectivityCheckInterval) {
      return of(this.isOnline$.value);
    }
    
    this.lastConnectivityCheck = now;
    this.isPerformingCheck = true;
    
    
    // Primary: same-origin manifest to avoid CORS/auth, bypass SW and caches
    const primaryCheckUrl = `/manifest.webmanifest?_=${Date.now()}`;
    const primaryCheck = this.http.get(primaryCheckUrl, { headers: { 'ngsw-bypass': 'true' } }).pipe(
      timeout(5000),
      map(() => {
        return true;
      }),
      catchError((error) => {
        // If we got a non-network error, we are likely online (e.g., 404/500)
        if (error && typeof error.status === 'number' && error.status !== 0) {
          return of(true);
        }
        return this.fallbackConnectivityCheck();
      })
    );

    return primaryCheck.pipe(
      tap(result => {
        this.isPerformingCheck = false;
        if (result) {
          this.consecutiveFailures = 0;
        } else {
          this.consecutiveFailures++;
        }
      }),
      catchError(() => {
        this.isPerformingCheck = false;
        this.consecutiveFailures++;
        return of(false);
      })
    );
  }

  /**
   * Enhanced fallback connectivity check with multiple strategies
   */
  private fallbackConnectivityCheck(): Observable<boolean> {
    // Prefer same-origin API endpoints to avoid CORS issues and SW cache
    const healthUrl = `/cache_hash.php?_=${Date.now()}`;
    const versionUrl = `/index.html?_=${Date.now()}`;

    const healthCheck = this.http.get(healthUrl, { headers: { 'ngsw-bypass': 'true' }, responseType: 'text' as 'json' }).pipe(
      timeout(3000),
      map(() => {
        return true;
      }),
      catchError((error) => {
        if (error && typeof error.status === 'number' && error.status !== 0) {
          return of(true);
        }
        return of(false);
      })
    );

    const versionCheck = this.http.get(versionUrl, { headers: { 'ngsw-bypass': 'true' }, responseType: 'text' as 'json' }).pipe(
      timeout(3000),
      map(() => {
        return true;
      }),
      catchError((error) => {
        if (error && typeof error.status === 'number' && error.status !== 0) {
          return of(true);
        }
        return of(false);
      })
    );

    // Try cache hash first, then index fallback
    return healthCheck.pipe(
      switchMap(result => result ? of(true) : versionCheck)
    );
  }

  /**
   * Update connectivity state with proper validation
   */
  private updateConnectivityState(isConnected: boolean): void {
    const currentState = this.isOnline$.value;
    
    // Only update if state actually changed or if we have multiple consecutive failures
    if (currentState !== isConnected || this.consecutiveFailures >= this.maxConsecutiveFailures) {
      this.isOnline$.next(isConnected);
      this.notifyServiceWorker(isConnected);
      
      // Reset consecutive failures on successful state change
      if (isConnected) {
        this.consecutiveFailures = 0;
      }
    }
  }

  private notifyServiceWorker(isOnline: boolean): void {
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        navigator.serviceWorker.controller.postMessage({
          type: 'CONNECTIVITY_STATUS',
          isOnline,
          timestamp: Date.now()
        });
      } catch (error) {
        console.error('[ConnectivityService] Error notifying service worker:', error);
      }
    }
  }

  /**
   * Wait for connectivity to be restored with improved polling
   */
  waitForConnection(): Observable<boolean> {
    return this.isOnline$.pipe(
      distinctUntilChanged(),
      switchMap(isOnline => {
        if (isOnline) {
          return of(true);
        } else {
          // Exponential backoff for offline polling
          const baseDelay = 2000; // Start with 2 seconds
          const maxDelay = 30000; // Max 30 seconds
          
          return timer(0, baseDelay).pipe(
            switchMap((attempt) => {
              const delay = Math.min(baseDelay * Math.pow(1.5, attempt), maxDelay);
              return timer(delay).pipe(
                switchMap(() => this.performConnectivityCheck())
              );
            }),
            filter(isConnected => isConnected),
            tap(() => {
              this.updateConnectivityState(true);
            }),
            takeUntil(this.isOnline$.pipe(filter(online => online)))
          );
        }
      }),
      shareReplay(1)
    );
  }

  /**
   * Cleanup method for proper resource management
   */
  destroy(): void {
    this.isDestroyed$.next(true);
    this.isDestroyed$.complete();
  }
} 